import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import AppDataGrid from "../../../components/common/AppDataGrid";

const emptyRateRow = {
  tax_percent: "",
  rcm_company_percent: "",
  rcm_party_percent: "",
  rate_master_detail_id: 0,
};

function AddGSTRateMaster() {
  const userData = sessionStorage.getItem("user");
  const userObj = JSON.parse(userData || "{}");
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const routeLocation = useLocation();
  const navigate = useNavigate();

  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // GST Code Types (GOODS / SERVICES)
  const [codeTypes, setCodeTypes] = useState([]);

  // GST Types for rows (SGST / CGST / IGST / UTGST / etc)
  const [gstTypes, setGstTypes] = useState([]);

  // HSN / SAC options
  const [hsnSacOptions, setHsnSacOptions] = useState([]);
  const [loadingHSN, setLoadingHSN] = useState(false);

  const [form, setForm] = useState({
    code_type: "", // "GOODS" / "SERVICES"
    code_type_id: "", // numeric
    hsn_sac_id: "", // hsn_sac_master_id
    hsn_sac_code: "",
    effective_date: "",
    is_active: "A",
    // key: gst_type_name → row
    rates: {},
  });

  // ---------- Helpers ----------
  const fmt3 = (val) => {
    if (val === "" || val === null || val === undefined) return "0.000";
    let num = String(val).replace(/[^0-9.]/g, "");
    if (num === "") num = "0";
    const n = Math.min(100, Math.max(0, parseFloat(num || "0")));
    return n.toFixed(3);
  };

  const onRateChange = (rowKey, field, value) => {
    const cleaned = value
      .replace(/[^\d.]/g, "")
      .replace(/^(\d*\.\d{0,3}).*$/, "$1");
    setForm((prev) => ({
      ...prev,
      rates: {
        ...prev.rates,
        [rowKey]: {
          ...(prev.rates[rowKey] || { ...emptyRateRow }),
          [field]: cleaned,
        },
      },
    }));
  };

  const onRateBlur = (rowKey, field) => {
    setForm((prev) => ({
      ...prev,
      rates: {
        ...prev.rates,
        [rowKey]: {
          ...(prev.rates[rowKey] || { ...emptyRateRow }),
          [field]: fmt3(prev.rates[rowKey]?.[field]),
        },
      },
    }));
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      code_type: "",
      code_type_id: "",
      hsn_sac_id: "",
      hsn_sac_code: "",
      effective_date: "",
      is_active: "A",
      rates: {},
    });
    setHsnSacOptions([]);
  };

  // ---------- Fetch GST Code Types ----------
  const fetchGstCodeTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/GetCodeType/");
      const data = Array.isArray(resp.data) ? resp.data : [];
      setCodeTypes(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setCodeTypes([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load GST Code Types");
    }
  };

  useEffect(() => {
    if (baseUrl) fetchGstCodeTypes();
  }, [baseUrl]);

  // ---------- Fetch GST Types (rows) ----------
  useEffect(() => {
    const fetchGstTypes = async () => {
      try {
        const resp = await axios.post(
          baseUrl + "/GetGSTType/",
          {},
          { headers: { "Content-Type": "application/json" } }
        );
        const data = Array.isArray(resp.data) ? resp.data : [];
        setGstTypes(data);

        // Ensure a rate object for each gst_type_name
        setForm((prev) => ({
          ...prev,
          rates: data.reduce((acc, t) => {
            const key = t.gst_type_name;
            acc[key] = prev.rates[key] || { ...emptyRateRow };
            return acc;
          }, { ...prev.rates }),
        }));
      } catch (e) {
        console.error(e);
        toast.error("Failed to load GST Types", { position: "top-right" });
        setGstTypes([]);
      }
    };

    if (baseUrl) {
      fetchGstTypes();
    }
  }, [baseUrl]);

  // ---------- Fetch HSN/SAC ----------
  useEffect(() => {
    const loadHsnSac = async () => {
      if (!form.code_type) {
        setHsnSacOptions([]);
        return;
      }
      setLoadingHSN(true);
      try {
        const resp = await axios.post(
          `${baseUrl}/get_hsn_sac_bycodetype/`,
          { code_type: form.code_type },
          { headers: { "Content-Type": "application/json" } }
        );

        if (Array.isArray(resp.data)) {
          setHsnSacOptions(resp.data);
        } else {
          setHsnSacOptions([]);
        }
      } catch (err) {
        console.error(err);
        if (err.response?.status === 404) {
          toast.info("No HSN/SAC codes found for selected Code Type", {
            position: "top-right",
          });
        } else {
          toast.error("Failed to load HSN/SAC codes", {
            position: "top-right",
          });
        }
        setHsnSacOptions([]);
      } finally {
        setLoadingHSN(false);
      }
    };

    if (baseUrl) {
      loadHsnSac();
    }
  }, [form.code_type, baseUrl]);

  // ---------- Load details in Edit mode ----------
  const fetchGstEditDetails = async (rate_master_id) => {
    try {
      const res = await axios.get(
        `${baseUrl}/main/get_gst_rate_byrate_master_id`,
        { params: { rate_master_id } }
      );

      const details = res.data?.data || [];
      const ratesObj = {};

      details.forEach((d) => {
        const key = d.gst_type_name?.toUpperCase();
        if (!key) return;

        ratesObj[key] = {
          tax_percent: fmt3(d.tax_percentage),
          rcm_company_percent: fmt3(d.rcm_own_percentage),
          rcm_party_percent: fmt3(d.rcm_party),
          rate_master_detail_id: d.rate_master_detail_id || 0,
        };
      });

      setForm((prev) => ({
        ...prev,
        rates: { ...prev.rates, ...ratesObj },
      }));
    } catch (err) {
      console.error("Edit GST details load failed", err);
      toast.error("Failed to load GST Rate Details", { position: "top-right" });
    }
  };

  useEffect(() => {
    const rec = routeLocation.state?.gstRate;
    const rate_master_id = routeLocation.state?.rate_master_id;

    if (!rec || !rate_master_id) return;

    setEditId(rate_master_id);

    setForm((prev) => ({
      ...prev,
      code_type: rec.code_type,
      code_type_id: rec.code_type_id,
      hsn_sac_id: rec.hsn_sac_master_id,
      hsn_sac_code: rec.hsn_sac_code,
      effective_date:
        rec.effect_date?.slice(0, 10) ||
        rec.effective_date?.slice(0, 10) ||
        "",
      is_active: rec.is_active || "A",
    }));

    fetchGstEditDetails(rate_master_id);
  }, [routeLocation.state]);

  // ---------- Duplicate check (CodeType + HSN + EffectiveDate) ----------
  const checkDuplicateCombination = async (
    code_type_id,
    hsn_sac_master_id,
    effective_date,
    currentRateMasterId
  ) => {
    try {
      const payload = {
        code_type_id: code_type_id || 0,
        hsn_sac_master_id:
          typeof hsn_sac_master_id === "string"
            ? parseInt(hsn_sac_master_id, 10) || 0
            : hsn_sac_master_id || 0,
        gst_type_id: 0, // master-level check
        flag: "GSTRM",  // 🔁 CHANGED from "M" → "GSTRM" to match DB function
        effective_date: effective_date,
        id: currentRateMasterId || 0,
      };

      const res = await axios.post(
        `${baseUrl}/finance/check_duplication_effective_date_account_id`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      console.log("Duplication API raw response:", res.data);

      // Safely convert result to a number
      let raw = res.data?.result;

      if (raw === undefined || raw === null) {
        if (
          res.data &&
          (res.data.code_type_id ||
            res.data.hsn_sac_master_id ||
            res.data.effective_date)
        ) {
          return 1;
        }
        return 0;
      }

      if (typeof raw === "string") {
        const match = raw.match(/-?\d+/);
        raw = match ? parseInt(match[0], 10) : 0;
      }

      const num = Number(raw);
      if (Number.isNaN(num)) return 0;

      return num; // >0 => duplicate, 0 => no duplicate
    } catch (err) {
      console.error("Duplicate check failed", err);
      toast.error("Failed to validate duplicate GST Rate.", {
        position: "top-right",
      });
      return -1; // signal error
    }
  };

  // ---------- Grid (AppDataGrid) for GST Rates ----------
  const rateRows = useMemo(
    () =>
      gstTypes.map((t) => {
        const key = t.gst_type_name;
        const row = form.rates[key] || { ...emptyRateRow };
        return {
          id: t.gst_type_id,
          gst_type_id: t.gst_type_id,
          gst_type_name: key,
          tax_percent: row.tax_percent,
          rcm_company_percent: row.rcm_company_percent,
          rcm_party_percent: row.rcm_party_percent,
        };
      }),
    [gstTypes, form.rates]
  );

  const rateColumns = useMemo(
    () => [
      {
        field: "gst_type_name",
        headerName: "GST Type",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <span className="fw-semibold">{params.row.gst_type_name}</span>
        ),
      },
      {
        field: "tax_percent",
        headerName: "Tax %",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          const rowKey = params.row.gst_type_name;
          return (
            <Form.Control
              inputMode="decimal"
              placeholder="0.000"
              value={form.rates[rowKey]?.tax_percent || ""}
              onChange={(e) =>
                onRateChange(rowKey, "tax_percent", e.target.value)
              }
              onBlur={() => onRateBlur(rowKey, "tax_percent")}
              maxLength={7}
            />
          );
        },
      },
      {
        field: "rcm_company_percent",
        headerName: "RCM Company %",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => {
          const rowKey = params.row.gst_type_name;
          return (
            <Form.Control
              inputMode="decimal"
              placeholder="0.000"
              value={form.rates[rowKey]?.rcm_company_percent || ""}
              onChange={(e) =>
                onRateChange(rowKey, "rcm_company_percent", e.target.value)
              }
              onBlur={() => onRateBlur(rowKey, "rcm_company_percent")}
              maxLength={7}
            />
          );
        },
      },
      {
        field: "rcm_party_percent",
        headerName: "RCM Party %",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => {
          const rowKey = params.row.gst_type_name;
          return (
            <Form.Control
              inputMode="decimal"
              placeholder="0.000"
              value={form.rates[rowKey]?.rcm_party_percent || ""}
              onChange={(e) =>
                onRateChange(rowKey, "rcm_party_percent", e.target.value)
              }
              onBlur={() => onRateBlur(rowKey, "rcm_party_percent")}
              maxLength={7}
            />
          );
        },
      },
    ],
    [form.rates]
  );

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.code_type || !form.hsn_sac_id || !form.effective_date) {
      toast.error("Please fill all required fields.", {
        position: "top-right",
      });
      return;
    }

    try {
      setSubmitting(true);

      // Resolve code_type_id
      let code_type_id = form.code_type_id;
      if (!code_type_id && form.code_type) {
        const found = codeTypes.find(
          (ct) =>
            ct.code_type === form.code_type ||
            ct.code_type_name === form.code_type
        );
        code_type_id = found?.code_type_id || "";
      }

      if (!code_type_id) {
        toast.error("Unable to resolve Code Type. Please re-select it.", {
          position: "top-right",
        });
        setSubmitting(false);
        return;
      }

      // Duplicate validation
      const dupResult = await checkDuplicateCombination(
        code_type_id,
        form.hsn_sac_id,
        form.effective_date,
        editId || 0
      );

      console.log("dupResult (final numeric)", dupResult);

      if (dupResult === -1) {
        setSubmitting(false);
        return;
      }

      if (dupResult > 0) {
        toast.error(
          "A GST Rate already exists with the same Code Type, HSN Code and Effective Date.",
          { position: "top-right" }
        );
        setSubmitting(false);
        return;
      }

      // Build detail lines
      const lines = gstTypes.map((t) => {
        const key = t.gst_type_name;
        const row = form.rates[key] || { ...emptyRateRow };
        return {
          rate_master_detail_id: row.rate_master_detail_id || 0,
          gst_type_id: t.gst_type_id,
          tax_type: key,
          tax_percent: parseFloat(fmt3(row.tax_percent)),
          rcm_company_percent: parseFloat(fmt3(row.rcm_company_percent)),
          rcm_party_percent: parseFloat(fmt3(row.rcm_party_percent)),
        };
      });

      // 1) Save master
      const masterPayload = {
        rate_master_id: editId || 0,
        code_type_id: code_type_id,
        hsn_sac_master_id: form.hsn_sac_id,
        effect_date: form.effective_date,
        created_by: userObj.user_name,
      };

      const masterUrl = `${baseUrl}/addupdate_gst_rate_master/`;
      const resMaster = await axios.post(masterUrl, masterPayload, {
        headers: { "Content-Type": "application/json" },
      });

      const rate_master_id = resMaster.data?.rate_master_id;
      if (!rate_master_id) {
        throw new Error(
          "rate_master_id not returned from /addupdate_gst_rate_master"
        );
      }

      // 2) Save details (bulk)
      const detailsPayload = {
        details: lines.map((ln) => ({
          rate_master_detail_id: ln.rate_master_detail_id || 0,
          rate_master_id: rate_master_id,
          gst_type_id: ln.gst_type_id,
          tax_percentage: ln.tax_percent,
          rcm_own_percentage: ln.rcm_company_percent,
          rcm_party: ln.rcm_party_percent,
          created_by: userObj.username || userObj.email || "system",
        })),
      };

      const detailsUrl = `${baseUrl}/addupdate_gst_rate_master_details/`;
      await axios.post(detailsUrl, detailsPayload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        editId ? "GST Rate updated successfully." : "GST Rate created successfully.",
        { position: "top-right" }
      );

      setEditId(null);
      resetForm();
      // navigate(-1);
    } catch (error) {
      console.error(error);
      const serverMsg =
        error?.response?.data?.error || error.message || "Unexpected error";
      toast.error(serverMsg, { position: "top-right" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row className="mb-2">
                <Col xs={12}>
                  <h6 className="commonSectionTitle">
                    {editId ? "Edit GST Rate" : "Add GST Rate"}
                  </h6>
                </Col>
              </Row>

              {/* Top controls */}
              <Row>
                {/* Code Type */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Code Type <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Select
                        required
                        value={form.code_type}
                        disabled={!!editId}
                        onChange={(e) => {
                          const value = e.target.value;
                          const found = codeTypes.find(
                            (ct) => ct.code_type === value
                          );
                          setForm((prev) => ({
                            ...prev,
                            code_type: value,
                            code_type_id: found?.code_type_id || "",
                            hsn_sac_id: "",
                            hsn_sac_code: "",
                          }));
                        }}
                      >
                        <option value="">Select Code</option>
                        {codeTypes.map((ct) => (
                          <option key={ct.code_type} value={ct.code_type}>
                            {ct.code_type_name || ct.code_type}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                {/* HSN / SAC Code */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        HSN Code <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Select
                        required
                        value={form.hsn_sac_id}
                        disabled={!form.code_type || loadingHSN || !!editId}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            hsn_sac_id: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select HSN Code</option>
                        {hsnSacOptions.map((opt) => (
                          <option
                            key={opt.hsn_sac_master_id}
                            value={opt.hsn_sac_master_id}
                          >
                            {opt.hsn_sac_code}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                {/* Effective Date */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Effective Date <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={form.effective_date}
                        required
                        disabled={!!editId}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            effective_date: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              {/* Grid for Rates (AppDataGrid) */}
              <Row className="mt-3">
                <Col xs={12}>
                  <AppDataGrid
                    columns={rateColumns}
                    rows={rateRows}
                    getRowId={(row) => row.id}
                    autoHeight
                    hideFooterSelectedRowCount
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                  />
                </Col>
              </Row>

              {/* Actions */}
              <div className="d-flex justify-content-between mt-3">
                <div>
                  <Button
                    type="button"
                    variant="info"
                    className="clearBtn"
                    onClick={resetForm}
                  
                  >
                    Reset
                  </Button>
                </div>
                <div>
                  <Button
                    type="button"
                    variant="danger"
                    className="secondaryBtn me-2"
                    onClick={() => navigate(-1)}
                  
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                  
                    variant="primary"
                    className="btn-success primaryBtn"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default AddGSTRateMaster;
